/**
 * 负责业务逻辑的管理
 */
import Util, { mergeOption, deepCopy } from '../../util/index.js';
import vueFunction from '../../util/vueFunction';
import { OpenAPI } from './api';
import ChainMap from './database/chainmap';
import TrendDB from './database/trenddb';
import KLineDB from './database/klinedb';
import {
  TREND_ONE,
  TREND_FIVE,
  KLINE_ONEMIN,
  KLINE_FIVEMIN,
  KLINE_FIFMIN,
  KLINE_THIMIN,
  KLINE_SIXMIN,
  KLINE_ONEDAY,
  KLINE_ONEWEEK,
  KLINE_ONEMONTH,
  KLINE_ONEYEAR,
  KLINE_PRICE_TYPE,
  FOOT_STATUS,
  MAIN_STATUS,
  TREND_TYPE,
  KLINE_TYPE,
  KEY_MAP
} from '../../viewmodel/index.js';
import { tradeSectionGrp, setSectionGrp } from '../cache.js';
import {
  checkSS,
  checkFutures,
  checkHKM,
  checkMRI,
  checkAmerican,
  getIndex,
  checkNumFixed,
  getTimeStamp,
  transformAmerican,
  checkExchange,
  getTime,
  checkAvg,
  isImportantIndex,
  checkTrademinGrp,
  transformTime,
  timeFormat,
  isKline,
} from '../util.js';
const {
  TREND_KEY,
  MA_KEY,
  JZ_KEY,
  KD_KEY,
  HH_KEY,
  AMOUNT_KEY,
  MACD_KEY,
  LIANGBI_KEY,
  BOLL_KEY,
  PSY_KEY,
  RSI_KEY,
  DMI_KEY,
  OBV_KEY,
  WR_KEY,
  VR_KEY,
  DMA_KEY,
  KDJ_KEY,
  ENE_KEY,
  ELECTRIC_KEY,
  FUNDFLOW_KEY,
  FUNDFLOWDIR_KEY,
} = KEY_MAP;
const {
  KLINE_NONE,
  KLINE_BEFORE,
  KLINE_AFTER,
} = KLINE_TYPE;
const { TREND_WD, TREND_MX, TREND_CJ } = TREND_TYPE;
const STOPT = 'STOPT';
export default class DataManager {
  constructor(props) {
    /*threshold控制请求频率，低于该时间间隔取本地缓存*/
    let { threshold, ...rest } = props;
    // 初始化store需要监听的属性
    this.store = {
      sealObj: {
        hqTypeCode: '',
        tradeStatus: '',
        basePx: 0, // 分时中线坐标值
        preClosePx: 0, // 昨收
        perHand: 1, // 每手股数
        ratioFixed: 2, // 数据保留的位数
        prod_name: '', // 股票名称
        prev_settlement: 0 // 昨结算
      },
      transitionMatrix: undefined, //dom节点transform的变换矩阵
      position: [0, 0], //dom节点相对于page的位置
      transformOrigin: [0, 0], //dom节点transform的变换原点
      baseSize: 11, // canvas的字体大小
      model: undefined, // 给context的数据，防止被占用
      status: undefined, // 网络请求状态，ERROR, LOADING, LOADED
      MAIN_STATUS: deepCopy([], MAIN_STATUS),
      FOOT_STATUS: deepCopy([], FOOT_STATUS),
      kLineMapKey: undefined, // k线图的具体类型
      business_type: undefined,//外汇的基本报价类型3为中间价，1为成交价，2为报价2为卖价
      mainContextKey: undefined, // 主视图类Key
      mainContext: undefined, // 主视图类
      footContextKey: undefined, // 辅视图类Key
      footContext: undefined, // 辅视图类
      isFullScreen: undefined, // 是否全屏展示
      isVertical: undefined, // 是否竖屏
      isShowDetail: undefined, // 是否显示五档明细
      isShowFundflow: undefined, // 是否显示五档成交
      isShowKlineRight: false, // 是否显示除复权按钮
      trendStatus: undefined, // 显示五档明细的Key
      klineStatus: undefined, // K线除复权的Key
      timeStamp: undefined, // 当前请求的时间戳，用作发布无加载刷新
      // throttle: 6000, // 定时请求的时间间隔，0表示不刷新统一在刚进入的时候设置
      isAutoRefresh: false, // 是否定时请求数据，默认K线不自动请求
      isActive: true, // 是否为激活状态
      detailChain: null, // 分时五档详情链表
      footChain: null, // 辅视图链表
      mainChain: null, // 主视图链表
      isPress: false, // 是否显示十字光标
      graphConfig: null, // 绘图实例参数，null则取通用config
      log: null, // 埋点实例
      /*** 绘图数据 ***/
      foreignInfo: null,//外汇板块数据储存
      code: 0,
      period: 0,
      start: undefined,
      end: undefined,
      current: undefined, // 当前光标索引
      len: undefined, // 缓存下min_time长度，左右滑动时用

      maxLen: 180, // 放大的最大长度100:200
      minLen: 30, // 缩小的最小长度30:200
      stepLen: 10, // 放大缩小的步长5:200
      moreDataStep: 400,//加载更多的步长
      normalLen: 100, // 初始化长度60:200

      pageX: undefined, // 鼠标移动时的点坐标pageX
      pageY: undefined, // 鼠标移动时的点坐标pageY

      xValue: null,//x轴算出后的值
      axisX: null, // X轴坐标值
      minTime: null, // 分时增量查询的min_time
      crc: undefined, // 分时增量查询的crc
      min_time: undefined, // 坐标时间轴
      trade_section_grp: undefined, // 分时收开盘时间
      singleAmount: undefined, // 单日分时数据量
      fiveAmount: undefined, // 五日分时数据量
      inAmerica: undefined, // 是否美股
      showAvg: undefined, // 是否显示均价线
      showIndexRF: undefined, // 是否展示分时指数上涨下跌红绿柱
      fundflow: null, // 缓存资金流数据，用以资金博弈资金流向计算

      avg_px: null, // 均价线数据
      last_px: null, // 最新价数据
      business_amount: null, // 成交量数据
      kline: null, // K线数据
      /*** 五档 ***/
      bid_grp: [], // 买单
      offer_grp: [], // 卖单
      /*** 明细 ***/
      tick: null,
      /*** 成交 ***/
      fundflowIn: [],
      fundflowOut: [],
      /*** MACD ***/
      dea: [],
      dif: [],
      macd: [],
      /*** KDJ ***/
      k: null,
      d: null,
      j: null,
      /*** KD密码 ***/
      kList: null,
      dList: null,
      /*** 9转 ***/
      jqRiseList: null,
      jqFallList: null,
      /*** 均线 ***/
      maData: null,
      /*** boll ***/
      bollData: null,
      /*** 晓鲸电波 ***/
      signals: [],
      /*** 分时资金博弈 ***/
      circulation_amount: undefined, // 资金博弈成交额
      superArr: null,
      largeArr: null,
      medArr: null,
      litArr: null,
      /*** 分时资金流向 ***/
      fundflowData: null,
      /*** 鸿鹄震荡信号 ***/
      swanSignals: [],
    };
    Object.seal(this.store.sealObj);
    Object.freeze(this.store.minLen);
    Object.freeze(this.store.maxLen);
    Object.freeze(this.store.normalLen);
    Object.freeze(this.store.stepLen);
    Object.freeze(this.store.model); // 给context的数据，防止被占用
    this.api = OpenAPI;
    // this.api = new OpenAPI(rest);
    this.trendDb = new TrendDB({ api: this.api, threshold });
    this.klineDb = new KLineDB({ api: this.api, threshold });
    [
      'getSealData',
      'getData',
      'getForeignInfo',
      'getMore',
      'getMainData',
      'getFootData',
      'getDetailData',
      'getKlineRightData',
    ].forEach(method => {
      this[method] = this[method].bind(this);
    });
    return {
      state: this.store,
      getSealData: this.getSealData,
      getData: this.getData,
      getMore: this.getMore,
      getMainData: this.getMainData,
      getFootData: this.getFootData,
      getDetailData: this.getDetailData,
      getKlineRightData: this.getKlineRightData,
      getForeignInfo: this.getForeignInfo
    }
  }
  /**
   * [_calcTrendFiveAxisX 根据分时数据计算五日线X轴坐标值]
   * @param  {[type]} trendData [description]
   * @return {[type]}           [description]
   */
  _calcTrendFiveAxisX(trendData, marketData) {
    const { min_time, code } = trendData;
    let { singleAmount } = marketData;
    const len = min_time.length;
    let point = 0;
    let axisXTemp = [];
    while (point < len) {
      let time = min_time[point].toString();
      const timeStr = time.replace(/\d{4}(\d{2})(\d{2})\d+/i, '$1-$2');
      // const timeStr = time.replace(/\w{4}\/(\w{2})\/(\w{2})\s\S+/i, '$1-$2');
      axisXTemp.push(timeStr);
      point += singleAmount;
    }
    // 当日分时包含开盘时间点，需要去重
    let axisX = Array.from(new Set(axisXTemp));
    marketData.axisX = axisX;
  }
  _calcMarketDetail(data, code) {
    let amount = 0;
    let { trade_section_grp: marketTime, inAmerica } = data;
    // if (checkFutures(code)) {
    //   marketTime = [{
    //     open_time: marketTime[0].open_time,
    //     close_time: marketTime[marketTime.length - 1].close_time
    //   }];
    //   // 默认为345条 具体数值按数据条数
    //   amount = 345;
    // } else
    if (inAmerica) {
      amount = data.singleAmount;
    } else {
      inAmerica = checkAmerican(code);
      // marketTime.sort((a, b) => {
      //   return a.open_time - b.open_time;
      // })
      for (let detail of marketTime) {
        let close = detail.close_time
        if (detail.open_time > detail.close_time) {
          close = close + 2400 // 第二天收盘时间加24h
        }
        amount += getTimeStamp(detail.open_time, close);
        if (inAmerica) {
          detail.open_time = transformAmerican({ time: detail.open_time });
          detail.close_time = transformAmerican({ time: detail.close_time });
        }
      }
    }
    // 计算分时X轴坐标值
    if (checkExchange(code)) {
      // 外汇
      data.axisX = marketTime.map(time => {
        return getTime(time.open_time) + '$' + getTime(time.close_time);
      }).join('/').split('$');
      data.axisX.splice(1, 0, '20:00');
    } else if (checkFutures(code)) {
      data.axisX = marketTime.map(time => {
        return getTime(time.open_time) + '$' + getTime(time.close_time);
      }).join('/').split('$');
    } else {
      // 分时横坐标修改，参见packages\controller\cache.js
      let finance_mic = code.split(".")[1];
      data.axisX = tradeSectionGrp[finance_mic].axisXNew
    }
    // 计算五日分时X轴坐标值
    data.inAmerica = inAmerica;
    data.singleAmount = amount;
    data.fiveAmount = amount * 5;
    return data;
  }
  _calcTrendAvg(trendData) {
    const { avg_px, code } = trendData;
    const { hqTypeCode } = this.store.sealObj;
    const { isShowTrendAvg } = this.store;
    if (!checkAvg(code, hqTypeCode) || isShowTrendAvg === false) {
      // 不展示均价线
      trendData.avg_px = null;
      trendData.showAvg = false;
    } else {
      trendData.showAvg = true;
    }
    trendData.showIndexRF = isImportantIndex(code, hqTypeCode)
  }
  async getForeignInfo(code, period, types) {
    const type = types.split('.')[0];
    let rst;
    if (this.store.foreignInfo == null) {
      rst = await this.api.block_elements(code)
      rst = rst.data.block_elements
    } else {
      rst = this.store.foreignInfo
    }
    const data = rst[code]
    const { fields } = rst
    const prod_codeIndex = getIndex(fields, 'prod_code');
    const business_typeIndex = getIndex(fields, 'business_type');
    const oneInfo = (data.filter(item => item[business_typeIndex] == (type == null ? 1 : type)))[0]
    const prod_code = oneInfo[prod_codeIndex];
    const hq_type_code_Index = getIndex(fields, 'hq_type_code');
    const hq_type_code = oneInfo[hq_type_code_Index];
    const ingredient = prod_code + '.' + hq_type_code.split('.')[0]
    this.store.code = ingredient
    this.store.foreignInfo = rst
    return ingredient
  }
  async getMarketDetail(code) {
    let finance_mic = !checkExchange(code) ? code.split(".")[1] : code;
    let marketDetail = null;
    // 所有港股都用HKM的配置，减少接口请求
    if (/HK/ig.test(finance_mic) && (!/SS/ig.test(finance_mic) || !/SZ/ig.test(finance_mic))) {
      tradeSectionGrp[finance_mic] = tradeSectionGrp['HKM']
    }
    if (!tradeSectionGrp[finance_mic]) {
      // 获取code股票的分时总量等数据
      let res = await this.api.marketDetail({ finance_mic });
      const { market_detail_prod_grp, trade_section_biz_grp } = res.data
      const code_grp = market_detail_prod_grp.find((grp) => {
        return code.startsWith(grp.prod_code)
      })
      let trade_section_grp;
      if (code_grp) {
        trade_section_grp = trade_section_biz_grp[code_grp.trade_time_rule][code_grp.trade_time_rule]
      } else {
        trade_section_grp = res.data.trade_section_grp
      }
      marketDetail = this._calcMarketDetail({ trade_section_grp }, code);
      setSectionGrp(finance_mic, marketDetail);
      return marketDetail;
    }
    let _cacheDetail = tradeSectionGrp[finance_mic];
    marketDetail = this._calcMarketDetail(_cacheDetail, code);
    return marketDetail;
  }
  async getSealData(code) {
    const rst = await this.api.real({ code, fields: 'hq_type_code,trade_status,prod_name,preclose_px,prev_settlement,business_data_grp' });
    const { fields, [code]: codeData } = rst;
    const hqTypeCodeIndex = getIndex(fields, 'hq_type_code');
    const perHandIndex = getIndex(fields, 'shares_per_hand');
    const preClosePxIndex = getIndex(fields, 'preclose_px');
    const tradeStatusIndex = getIndex(fields, 'trade_status');
    const prodNameIndex = getIndex(fields, 'prod_name');
    const prevSettlementIndex = getIndex(fields, 'prev_settlement');
    const tradeStatus = codeData[tradeStatusIndex];
    const hqTypeCode = codeData[hqTypeCodeIndex];
    const preClosePx = codeData[preClosePxIndex];
    const prev_settlement = codeData[prevSettlementIndex];
    const basePx = prev_settlement || preClosePx;
    const prod_name = codeData[prodNameIndex];
    let perHand = codeData[perHandIndex] || 1;
    if (checkFutures(code) || checkHKM(code)) {
      // 期货港股每手1股
      perHand = 1;
    }
    this.store.sealObj.hqTypeCode = hqTypeCode;
    this.store.sealObj.perHand = perHand;
    this.store.sealObj.preClosePx = preClosePx;
    this.store.sealObj.tradeStatus = tradeStatus;
    this.store.sealObj.basePx = basePx;
    this.store.sealObj.prod_name = prod_name;
    this.store.sealObj.prev_settlement = prev_settlement;
  }
  /*
  处理五档明细数据
   */
  _calcDetailData(trendData) {
    try {
      const { data, trendStatus } = trendData;
      if (!trendStatus) return {};
      let rst = { trendStatus };
      const { perHand, ratioFixed, preClosePx } = this.store.sealObj;
      // 计算颜色
      const calc = a => {
        if (a.price) {
          a.color = a.price < preClosePx ? 0 : 1;
        }
      };
      /**
       * 改变数组地址，触发订阅
       * offer_grp,bid_grp,tick,fundflowIn,fundflowOut
       */
      switch (trendStatus) {
        case TREND_WD:
          const { circulation_amount } = data;
          const offer_grp = [...data.offer_grp];
          const bid_grp = [...data.bid_grp];
          offer_grp.forEach(calc);
          bid_grp.forEach(calc);
          rst = {
            ...rst,
            circulation_amount,
            offer_grp,
            bid_grp,
          };
          break;
        case TREND_MX:
          const tick = [...data.tick];
          tick.forEach(calc);
          rst = {
            ...rst,
            tick,
          };
          break;
        case TREND_CJ:
          const fundflowIn = [...data.fundflowIn];
          const fundflowOut = [...data.fundflowOut];
          rst = {
            ...rst,
            fundflowIn,
            fundflowOut,
          };
          break;
        default:
          break;
      }
      return rst;
    } catch (e) {

      return rst;
    }
  }
  /*
  计算分时成交量柱体颜色
   */
  _calcTrendAmountColor(trendData) {
    if (trendData.hasOwnProperty('business_amount')) {
      let _data = trendData.business_amount;
      if (Util.isArray(_data)) {
        const { last_px = this.store.last_px } = trendData;
        const preClosePx = this.store.sealObj.preClosePx
        const len = _data.length;
        if (len === 0) {
          return;
        }
        _data[0].barColor = last_px[0].value > preClosePx ? 1 : 0;
        for (let i = 1; i < len; i++) {
          if (last_px[i].value < last_px[i - 1].value) {
            _data[i].barColor = 0;
          } else {
            _data[i].barColor = 1;
          }
        }
      }
    }
  }

  //store拼接计算加载的数据
  async _calcKlineMoreData({ oldData, newData }) {
    let realData = {};
    const data = newData
    const storeData = oldData
    await Object.entries(data).forEach(function(item) {
      const key = item[0]
      const value = item[1]
      if (Util.isArray(value)) {
        realData[key] = value.concat(storeData[key])
      } else if (Util.isObject(value)) {
        if (Util.isDef(value)) {
          realData[key] = {};
          Object.entries(value).forEach(function(itemObj) {
            const keyC = itemObj[0]
            realData[key][keyC] = [];
            const valueC = itemObj[1]
            realData[key][keyC] = valueC.concat(storeData[key][keyC])
          })
        } else {
          realData[key] = Object.assign(value, storeData[key])
        }
      } else {
        realData[key] = value
      }
    })
    return realData
  }
  /*
  计算K线成交量柱体颜色
   */
  _calcKlineAmountColor(klineData) {
    const { business_amount = [], kline = [] } = klineData;
    const len = kline.length;
    if (len === 0) {
      return;
    }
    const kData = kline[0];
    const { close_px, open_px } = kData;
    if (close_px > open_px) {
      business_amount[0].barColor = kData.kColor = 1;
    } else {
      business_amount[0].barColor = kData.kColor = 0;
    }
    for (let i = 1; i < len; i++) {
      let color;
      const kData = kline[i];
      const { close_px, open_px } = kData;
      if (close_px > open_px) {
        color = 1;
      } else if (close_px < open_px) {
        color = 0;
      } else {
        const prevKData = kline[i - 1];
        const { close_px: prev_close_px } = prevKData;
        if (prev_close_px < close_px) {
          color = 1;
        } else {
          color = 0;
        }
      }
      business_amount[i].barColor = kData.kColor = color;
    }
  }
  /*
  计算MACD的柱体颜色
   */
  _calcMACDColor(_data) {
    const len = _data.length;
    if (Util.isArray(_data) && len > 1) {
      _data.forEach(item => {

        item.barColor = item.value >= 0 ? 1 : 2
      })
      // const compareVal = (v1, v2) => {
      //   return v2 < v1 ? (v2 < 0 ? 2 : 4) : (v2 < 0 ? 5 : 3);
      // };
      // _data[0].barColor = compareVal(_data[0].value, _data[1].value);
      // _data.reduce((prev, item) => {
      //   const value = item.value;
      //   item.barColor = compareVal(prev, value);
      //   return value;
      // });
    }
  }
  /*
  设置分时start和end
   */
  _setTrendPosition(trendData, marketData, period) {
    const position = {};
    // 当日分时条数为241 历史分时为240条
    let end = period === TREND_ONE ? (marketData.singleAmount + 1) : (marketData.fiveAmount + 2);
    position.start = 0;
    let len = 0;
    if (trendData.hasOwnProperty('min_time')) {
      len = trendData.min_time.length;
    }
    position.end = Math.max(len, end);
    position.len = len
    position.current = Math.max(0, len - 1);
    return position;
  }
  /*
  设置K线start和end
  同时设置axisX的坐标值
   */
  _setKlinePosition(data, isRestPosition) {
    const { normalLen, code } = this.store;
    const axisX = [];
    const { min_time } = data;
    const position = {};
    position.len = 0;
    let start = 0, end = 0;
    if (min_time && min_time.length > 0) {
      const len = min_time.length;
      start = Math.max(0, len - normalLen);
      end = start + normalLen;
      const axisXEnd = Math.min(len, end);
      const isAmerican = checkAmerican(code);
      // 光标移动时再转换时间，防止数据过大造成耗时过长
      axisX.push(transformTime({ timeStamp: min_time[start], code, isAmerican }));
      axisX.push(transformTime({ timeStamp: min_time[axisXEnd - 1], code, isAmerican }));
      // axisX.push(min_time[start]);
      // axisX.push(min_time[axisXEnd - 1]);
      position.len = len;
    } else {
      const code = this.store.code;
      const isAmerican = checkAmerican(code);
      const time = timeFormat(new Date(), 'yyyy/MM/dd');
      // const time = transformTime({code, isAmerican});
      axisX.push(time);
      axisX.push(time);
    }
    if (isRestPosition) {
      position.start = start;
      position.end = end;
      position.current = Math.max(0, end - 1);
    }
    position.axisX = axisX;
    return position;
  }
  //获取更多数据
  async getMore(code, period, param = {}, isRestPosition = false) {
    let data, dataLen;
    let position, marketData = {};
    let promiseQueue = [], positionObserve;
    switch (period) {
      // case TREND_ONE://分时
      // case TREND_FIVE://五日
      // case KLINE_ONEMIN://一分钟
      // case KLINE_FIVEMIN://5分钟
      // case KLINE_FIFMIN://15分钟
      // case KLINE_THIMIN://30分钟
      // case KLINE_SIXMIN://60分钟
      case KLINE_ONEWEEK://周线
      case KLINE_ONEMONTH://月线
      case KLINE_ONEYEAR:
      case KLINE_ONEDAY://  日线
        if (!checkExchange(code)) {
          marketData = await this.getMarketDetail(code);
        }
        // promiseQueue.push(Promise.resolve(this.klineDb.getKlineDataMore(param)));
        promiseQueue.push(Promise.resolve(this.klineDb.getKline(code, period, { ...param, ...{ isMore: true } })));
        const electric = this.store.footChain.has(a => a.key === ELECTRIC_KEY);
        if (electric) {
          // 包含日K电波模块且有用户权限
          promiseQueue.push(Promise.resolve(this.getXJDBData()));
        } else {
          promiseQueue.push(Promise.resolve({}));
        }
        const [klineData, signals] = await Promise.all(promiseQueue);
        dataLen = klineData.min_time.length
        if (klineData.code != this.store.code || this.store.period != period) {// || param.date!=klineData.min_time[moreDataStep+1]
          return
        }
        if (dataLen != 0) {
          this._calcKlineAmountColor(klineData)
        }
        const endKlineData = await this._calcKlineMoreData({ oldData: this.store, newData: klineData })
        // positionObserve = resizeObserver(curryDebounce(this._calcKlineAmountColor(klineData), this, 1000));
        // positionObserve.observe(document.body);//防抖
        // this._calcKlineAmountColor(klineData);
        position = await this._setKlinePosition(endKlineData, isRestPosition);
        data = { ...marketData, ...endKlineData, ...position, ...signals };
        break
      default:
        break;
    }
    data.timeStamp = new Date().getTime();
    await this.setStore(data);
    return dataLen
    // return data.min_time.length
  }
  /**
   * [getData 切换code或period]
   * @param  {[type]} code                   [description]
   * @param  {[type]} period                 [description]
   * @param  {[type]} options.isRestPosition [是否重新设置start和end，切换除复权时不重置position信息]
   * @param  {[type]} options.isAppend       [分时和五日分时append请求]
   * @return {[type]}                        [description]
   */
  async getData(
    code,
    period,
    {
      isRestPosition = true,
      isAppend = false,
    } = {},
    params
  ) {
    if (this.store.code !== code && code) {
      // this.api.summary('N');
      // 切换code时初始化常规数据
      this.store.code = code;
      this.store.period = period;
      const { isShowFundflow, sealObj } = this.store;
      const { hqTypeCode } = sealObj;
      const isSS = checkSS(code) && !checkMRI(code, hqTypeCode);
      this.store.isShowKlineRight = isSS; // 沪深个股支持除复权切换
      if (isSS) {
        this.getKlineRightData(KLINE_BEFORE)
        // if (!isSS) {
        // this.getKlineRightData(KLINE_NONE);
      }
      if (isSS && isShowFundflow) {
        // 沪深个股才默认显示成交
        this.store.detailChain = new ChainMap([
          TREND_WD,
          TREND_MX,
          TREND_CJ,
        ]);
      } else {
        this.store.detailChain = new ChainMap([
          TREND_WD,
          TREND_MX,
        ]);
      }
    }
    if (!isAppend) {
      this.store.sealObj.ratioFixed = await vueFunction.getToFixed(code);
      this.store.period = period;
      this.store.isShowDetail = false;
    } else {
      // 增量查询先查询股票交易状态
      await this.getSealData(code);
    }
    const { perHand, preClosePx, hqTypeCode, tradeStatus } = this.store.sealObj;
    // 交易状态：交易（连续撮合）|开始集合竞价|市场启动（初始化之后，集合竞价前）时保持轮询状态
    if (isAppend && !/(^TRADE$)|(^OCALL$)|(^START$)/i.test(tradeStatus)) {
      const _this = this
      // 仅对ENDTR状态进行是否轮询的特殊处理
      if (!/ENDTR/.test(tradeStatus)) {
        _this.store.endtrPolling = ''
        _this.store.specialTimeOutId = ''
        return
      }
      if (!_this.store.specialTimeOutId && /ENDTR/.test(tradeStatus)) {
        // 定时，20min内仍然获取新数据（港股需要时间更长）
        _this.store.specialTimeOutId = window.setTimeout(() => {
          _this.store.endtrPolling = true
          return
        }, 1200000)
      } else if (_this.store.endtrPolling && /ENDTR/.test(tradeStatus)) {
        // 20min后清除定时器且不再请求
        window.clearTimeout(_this.store.specialTimeOutId)
        return
      }
    }
    const { crc, minTime, end, min_time } = this.store;
    let isAutoRefresh = true;
    let data;
    let marketData, trendData, fundflowData, detailData, position, kline, klineDay = {};
    const promiseQueue = [];
    switch (period) {
      //分时
      case TREND_ONE:
        if (Util.isValidNumber(preClosePx) && !checkFutures(code)) {
          // 分时basePx取自real接口
          // 需重置五日分时kline接口设置的basePx
          this.store.sealObj.basePx = preClosePx;
        }
        // 获取五档明细status
        let isShowDetail = false;
        const trendStatus = this.getTrendStatus(null);
        if (trendStatus) {
          this.store.trendStatus = trendStatus;
        }
        marketData = await this.getMarketDetail(code);
        promiseQueue.push(Promise.resolve(this.trendDb.getTrend(code, period, { isAppend, crc, minTime })));
        if (checkSS(code) && !checkMRI(code, hqTypeCode)) {
          // 沪深个股展示五档明细
          isShowDetail = true;
          promiseQueue.push(Promise.resolve(this.trendDb.getTrendDetail(trendStatus, perHand)));
        } else {
          promiseQueue.push(Promise.resolve({}));
        }
        // if (tradeStatus !== STOPT && checkTrademinGrp(hqTypeCode)) {
        // 展示资金博弈，资金流向
        // promiseQueue.push(Promise.resolve(this.trendDb.getTrademinGrp()));
        // } else {
        // promiseQueue.push(Promise.resolve({}));
        // }
        [trendData, detailData, fundflowData] = await Promise.all(promiseQueue);
        detailData = this._calcDetailData(detailData); // 处理五档明细数据
        this._calcTrendAvg(trendData); // 判断是否显示均线
        this._calcTrendAmountColor(trendData); // 计算成交量颜色
        isRestPosition && (position = this._setTrendPosition(trendData, marketData, period)); // 设置分时start和end
        const { jqRiseList, jqFallList } = await this.getJQJZData(code, period, trendData.min_time); // 计算9转信号
        data = { ...marketData, ...trendData, ...fundflowData, ...detailData, jqRiseList, jqFallList, ...position, isShowDetail };
        break;
      //五日
      case TREND_FIVE:
        marketData = await this.getMarketDetail(code);
        trendData = await this.trendDb.getTrend(code, period, { isAppend, crc, minTime });
        const { basePx, ...rest } = trendData;
        if (Util.isValidNumber(basePx) && !checkFutures(code)) {
          // 非期货的五日分时basePx取自Kline接口
          // 需重置初始化时real接口设置的basePx
          this.store.sealObj.basePx = basePx;
        }
        this._calcTrendAvg(rest);
        this._calcTrendAmountColor(rest);
        this._calcTrendFiveAxisX(rest, marketData);
        isRestPosition && (position = this._setTrendPosition(rest, marketData, period));
        data = { ...marketData, ...position, ...rest };
        break;
      case KLINE_ONEMIN://一分钟
      case KLINE_FIVEMIN://5分钟
      case KLINE_FIFMIN://15分钟
      case KLINE_THIMIN://30分钟
      case KLINE_SIXMIN://60分钟
      //  日线
      case KLINE_ONEDAY:
      // promiseQueue.push(Promise.resolve(this.klineDb.getKline(code, period, { ...params,isAppend, crc, minTime,period })));
      // const [dayKline] = await Promise.all(promiseQueue);
      // this._calcKlineAmountColor(dayKline);
      // isRestPosition && (position = this._setKlinePosition(dayKline, isRestPosition));
      // data = { ...dayKline, ...position };
      // break;
      case KLINE_ONEWEEK://周线
      case KLINE_ONEMONTH://月线
      case KLINE_ONEYEAR://年线
        isAutoRefresh = false;
        if (!checkExchange(code)) {
          marketData = await this.getMarketDetail(code);
        }
        promiseQueue.push(Promise.resolve(this.klineDb.getKline(code, period, { ...params, isAppend, crc, minTime, period })));
        const electric = this.store.footChain.has(a => a.key === ELECTRIC_KEY);
        if (electric) {
          // 包含日K电波模块且有用户权限
          promiseQueue.push(Promise.resolve(this.getXJDBData()));
        } else {
          promiseQueue.push(Promise.resolve({}));
        }
        const [klineData, signals] = await Promise.all(promiseQueue);
        this._calcKlineAmountColor(klineData);
        position = this._setKlinePosition(klineData, isRestPosition);
        //分钟线增量加载数据新数据不绘制的问题
        if (isAppend && min_time.length != klineData.min_time.length) {
          position.end = klineData.min_time.length;
        }
        data = { ...marketData, ...klineData, ...position, ...signals };
        break;
      default:
        break;
    }
    data.timeStamp = new Date().getTime();
    data.isAutoRefresh = true;
    this.setStore(data);
  }
  async getDetailData(key) {
    const { code, period } = this.store;
    const trendStatus = this.getTrendStatus(key);
    /**
     * 先更新trendStatus对应tab，再更新数据
     */
    if (trendStatus) {
      this.store.trendStatus = trendStatus;
      const { perHand } = this.store.sealObj;
      const rst = await this.trendDb.getTrendDetail(trendStatus, perHand);
      if (this.store.trendStatus === rst.trendStatus) {
        const data = this._calcDetailData(rst);
        this.setStore({ ...data, code, period });
      }
    }
  }
  /*
  获取trendStatus
   */
  getTrendStatus(trendStatus) {
    const { detailChain } = this.store;
    let _trendStatus;
    if (Util.isUndefined(trendStatus)) {
      // 获取下一个指针
      _trendStatus = detailChain.next();
    } else if (Util.isNull(trendStatus)) {
      // 获取当前指针
      _trendStatus = detailChain.get();
    } else {
      // 获取指定指针
      _trendStatus = detailChain.move(a => a === trendStatus);
    }
    return _trendStatus;
  }
  /*
  设置除复权状态
   */
  getKlineRightData(key) {
    const { code, period } = this.store;
    const klineStatus = this.klineDb.getKlineStatus(key);
    /**
     * 先更新klineStatus对应tab，再更新数据
     */
    if (Util.isDef(klineStatus)) {
      this.store.klineStatus = klineStatus.value;
    }
  }
  /*
  功能切换
   */
  getMainData(key) {
    switch (key) {
      case HH_KEY:
        this.getSwanData();
        break;
      default:
        break;
    }
  }
  getFootData(key) {
    switch (key) {
      case FUNDFLOW_KEY:
        this.getFundCapitalData();
        break;
      case FUNDFLOWDIR_KEY:
        this.getFundFlowData();
        break;
      case MACD_KEY:
        this.getMacdData();
        break;
      // 如果值为DMA_KEY，就调用this.getDmaData()
      case DMA_KEY:
        this.getDmaData();
        break;
      // 如果值为VR_KEY，就调用this.getVrData()
      case VR_KEY:
        this.getVrData();
        break;
      // 如果值为WR_KEY,就调用this.getWrData()
      case WR_KEY:
        this.getWrData();
        break;
      // 如果值为OBV_KEY，就调用this.getObvData()
      case OBV_KEY:
        this.getObvData();
        break;
      // 如果值为 DMI_KEY,就调用this.getDmiData()
      case DMI_KEY:
        this.getDmiData();
        break;
      case RSI_KEY:
        this.getRsiData();
        break;
      case PSY_KEY:
        this.getPsyData();
        break;
      case BOLL_KEY:
        this.getBollData();
        break;
      case LIANGBI_KEY:
        this.getLiangbiData();
        break;
      case AMOUNT_KEY:
        this.getTurnoverData();
        break;
      case KDJ_KEY:
        this.getKDJData();
        break;
      case ENE_KEY:
        this.getENEData();
        break;
      default:
        break;
    }
  }
  /*
  成交量
   */
  getTurnoverData() { }
  /*
  MACD数据
  包括分时MACD和K线MACD
   */
  getMacdData() {
    let data;
    const { last_px, code, period, kline } = this.store;
    /*
    K线的MACD与分时计算方式有差异
    分时取最新价计算，K线取收盘价计算
     */
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH,
      KLINE_ONEYEAR,
      KLINE_PRICE_TYPE
    ].some(a => a === period);
    if (isKLine) {
      data = kline.map(a => a.close_px);
    } else {
      data = last_px.map(a => a.value);
    }
    const len = data.length;
    if (!Util.isValidArray(data)) {
      return;
    }
    const lastPx = data[0];
    let dif = [{ value: 0 }], dea = [{ value: 0 }], macd = [{ value: 0 }], ema12 = [lastPx], ema26 = [lastPx];
    for (let i = 1; i < len; i++) {
      const ema12Val = ema12[i - 1] * 11 / 13 + data[i] * 2 / 13;
      const ema26Val = ema26[i - 1] * 25 / 27 + data[i] * 2 / 27;
      ema12.push(ema12Val);
      ema26.push(ema26Val);
      const difVal = ema12Val - ema26Val;
      dif.push({ value: difVal });
      const deaVal = dea[i - 1].value * 8 / 10 + difVal * 2 / 10;
      dea.push({ value: deaVal });
      macd.push({ value: (difVal - deaVal) * 2 });
    }
    this._calcMACDColor(macd);
    let params = 'MACD(12,26,9)'
    this.setStore({
      dea,
      dif,
      macd,
      code,
      period,
      params
    });
  }
  // RSI计算
  getRsiData() {
    let data;
    const { last_px, code, period, kline } = this.store;
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH,
      KLINE_ONEYEAR,
      KLINE_PRICE_TYPE
    ].some(a => a === period);
    if (isKLine) {
      data = kline.map(a => a.close_px);
    } else {
      data = last_px.map(a => a.value);
    }
    let ticks = [], closeData = []
    closeData.push(data)
    closeData[0].forEach(val => {
      ticks.push(val)
    })
    let lastClosePx = ticks[0];
    let days = [6, 12, 24], result = {};
    for (let i = 0; i < ticks.length; i++) {
      let c = ticks[i];
      let m = Math.max(c - lastClosePx, 0), a = Math.abs(c - lastClosePx);
      for (let di = 0; di < days.length; di++) {
        let d = days[di];
        if (!result.hasOwnProperty("rsi" + d)) {
          result["lastSm" + d] = result["lastSa" + d] = 0;
          result["rsi" + d] = [0];
        } else {
          result["lastSm" + d] = (m + (d - 1) * result["lastSm" + d]) / d;
          result["lastSa" + d] = (a + (d - 1) * result["lastSa" + d]) / d;
          if (result["lastSa" + d] != 0) {
            result["rsi" + d].push(result["lastSm" + d] / result["lastSa" + d] * 100);
          } else {
            result["rsi" + d].push(0);
          }
        }
      }
      lastClosePx = c;
    }

    let rsi6 = result["rsi6"], rsi12 = result["rsi12"], rsi24 = result["rsi24"];
    for (let i = 0; rsi6 && i < rsi6.length; i++) {
      if (i == 0) {
        rsi6[i] = null;
      } else {
        //rsi6[i]=rsi6[i].toFixed(2);
      }
    }
    for (let i = 0; rsi12 && i < rsi12.length; i++) {
      if (i == 0) {
        rsi12[i] = null;
      } else {
        //rsi12[i]=rsi12[i].toFixed(2);
      }
    }
    for (let i = 0; rsi24 && i < rsi24.length; i++) {
      if (i == 0) {
        rsi24[i] = null;
      } else {
        //rsi24[i]=rsi24[i].toFixed(2);
      }
    }
    let rsi0 = [{ value: 0 }], rsi1 = [{ value: 0 }], rsi2 = [{ value: 0 }]
    for (let i = 0; rsi6 && i < rsi6.length; i++) {
      rsi0.push({ value: rsi6[i + 1] });
      rsi1.push({ value: rsi12[i + 1] });
      rsi2.push({ value: rsi24[i + 1] })
    }
    rsi6 = rsi0
    rsi12 = rsi1
    rsi24 = rsi2
    let params = 'RSI(6,12,24)'
    this.setStore({
      code,
      period,
      rsi6,
      rsi12,
      rsi24,
      params
    });

  }
  /*
  (4)	WR 威廉指标
  WR1:100*(HHV(HIGH,N)-CLOSE)/(HHV(HIGH,N)-LLV(LOW,N));
  （N天内最高价-当日的收盘价）/(N天内的最高价-N天内的最低价)
  WR2:100*(HHV(HIGH,N1)-CLOSE)/(HHV(HIGH,N1)-LLV(LOW,N1));
  默认参数：N= 10.00，N1=6
  */
  // 量比
  getLiangbiData() {
    // 分时取最新价
    const { last_px, code, business_amount, period, kline } = this.store
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH,
      KLINE_ONEYEAR,
      KLINE_PRICE_TYPE
    ].some(a => a == period)
    if (isKLine) {
      let kAmount = business_amount.map(a => a.value)
      let klen = kAmount.length
    } else {
      let trendAmount = business_amount.map(a => a.value)
      let trendlen = trendAmount.length;
    }
    // 量比=（现成交总手数 / 现累计开市时间(分) ）/ 过去5日平均每分钟成交量
    // 现成交总手数 / 现累计开市时间(分)
    let trendArr = []
    for (let i = 0; i < trendlen; i++) {
      trendArr.push(trendAmount[trendlen - 1]) / trendlen
    }
    //  过去5日平均每分钟成交量
    let kAmountSum = 0, kmin5 = [], liangbi = []
    kAmount.forEach(val => {
      kAmountSum += val
      kmin5.push(kAmountSum / 5 * 240)
    })
    for (let i = 0; i < trendlen; i++) {
      liangbi.push(trendArr[i] / kmin5[i])
    }
    this.setStore({
      code,
      liangbi
    })
  }
  getWrData() {
    let data;
    let highData = [], lowData = [], closeData = [];
    const { code, period, kline } = this.store
    kline.forEach(val => {
      highData.push(val.high_px)
      lowData.push(val.low_px)
      closeData.push(val.close_px)
    })
    const len = kline.length;
    let hhv10 = [0], llv10 = [0], wr1 = [], hhv6 = [0], llv6 = [0], wr2 = []
    for (let i = 1; i < len; i++) {
      if (i < 10) {
        hhv10.push(Math.max.apply(null, highData.slice(0, i)))
        llv10.push(Math.min.apply(null, lowData.slice(0, i)))
      } else {
        hhv10.push(Math.max.apply(null, highData.slice(0, i + 1).slice(-10)))
        llv10.push(Math.min.apply(null, lowData.slice(0, i + 1).slice(-10)))
      }
    }
    for (let i = 1; i < len; i++) {
      if (i < 6) {
        hhv6.push(Math.max.apply(null, highData.slice(0, i)))
        llv6.push(Math.min.apply(null, lowData.slice(0, i)))
      } else {
        hhv6.push(Math.max.apply(null, highData.slice(0, i + 1).slice(-6)))
        llv6.push(Math.min.apply(null, lowData.slice(0, i + 1).slice(-6)))
      }
    }
    let wr10 = [{ value: 0 }], wr6 = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      const wr1Base = hhv10[i] - llv10[i] == 0 ? 1 : hhv10[i] - llv10[i]
      const wr2Base = hhv6[i] - hhv6[i] == 0 ? 1 : hhv6[i] - llv6[i]
      // wr1[i] = 100 * (hhv10[i] - closeData[i]) / (hhv10[i] - llv10[i])
      // wr2[i] = 100 * (hhv6[i] - closeData[i]) / (hhv6[i] - llv6[i])
      wr1[i] = 100 * (hhv10[i] - closeData[i]) / wr1Base
      wr2[i] = 100 * (hhv6[i] - closeData[i]) / wr2Base
      wr10[i] = { value: wr1[i] }
      wr6[i] = { value: wr2[i] }
    }
    let params = 'WR(10,6)'
    this.setStore({
      wr10,
      wr6,
      code,
      period,
      params
    })
  }
  /**
   *
   * 计算移动平均线指标, ma的周期为days
   *
   * @method MA
   * @param {Array} ticks
   * @param Number days
   * 一维数组类型，每个元素为当前Tick的收盘价格
   * @return {Array} mas
   *
   /* VR 成交量变异率
   TH:=SUM(IF(CLOSE>REF(CLOSE,1),VOL,0),N);
   N个交易日里，股价上涨时的成交量成为AV，N交易日以内，总和成为th；
   TL:=SUM(IF(CLOSE<REF(CLOSE,1),VOL,0),N);
   N个交易日里，股价下降时的成交量成为BV，N交易日以内，总和成为tl；
   TQ:=SUM(IF(CLOSE=REF(CLOSE,1),VOL,0),N);
   N个交易日里，股价不涨不跌时的成交量成为CV，N交易日以内，总和成为tq
   VR:100*(TH*2+TQ)/(TL*2+TQ);
   MAVR:MA(VR,M);
   默认参数：N= 26.00，M=6
   */
  // getVrData() {
  //   let volVal = [], closeVal = []
  //   const { last_px, business_amount, code, period, kline } = this.store;

  //   let volVal1 = []
  //   const isKLine = [
  //     KLINE_ONEMIN,
  //     KLINE_FIVEMIN,
  //     KLINE_FIFMIN,
  //     KLINE_THIMIN,
  //     KLINE_SIXMIN,
  //     KLINE_ONEDAY,
  //     KLINE_ONEWEEK,
  //     KLINE_ONEMONTH,
  //     KLINE_ONEYEAR
  //   ].some(a => a === period)
  //   if (isKLine) {
  //     closeVal = kline.map(a => a.close_px);
  //     volVal = kline.map(a => a.business_amount)
  //   } else {
  //     closeVal = last_px.map(a => a.value)
  //     volVal = business_amount.map(a => a.value)
  //     // for (let i = 1; i < volVal1.length; i++) {
  //     //   volVal[i] = volVal1[i-1]
  //     //   volVal[volVal1.length - 1] = volVal1[volVal1.length - 1]
  //     // }
  //     // volVal[0] = 0
  //   }
  //   /*
  //  K线的VR与分时计算方式有差异
  //  分时取最新价计算，K线取收盘价计算
  //   */
  //   let ticks = []
  //   for (let i = 0; i < closeVal.length; i++) {
  //     ticks.push({ close: closeVal[i], vol: volVal[i] })
  //   }
  //   let vr = [], mavr = [];
  //   let avs = 0, bvs = 0, cvs = 0;
  //   const N = 26, M = 6;
  //   let tickBegin = N - 1;
  //   let mavrBegin = M - 1;
  //   let start = 0, end = M;
  //   for (let i = 0; i < ticks.length; i++) {
  //     let c = parseFloat(ticks[i].close);
  //     let vol = parseFloat(ticks[i].vol);
  //     if (i == 0) {
  //       cvs += vol;
  //     } else {
  //       if (c > ticks[i - 1].close) {
  //         avs += vol;
  //       } else if (c < ticks[i - 1].close) {
  //         bvs += vol;
  //       } else {
  //         cvs += vol;
  //       }
  //     }
  //     vr.push(100 * (avs * 2 + cvs) / (bvs * 2 + cvs));
  //     if (i == tickBegin) {
  //       cvs -= ticks[i - tickBegin].vol;
  //     }
  //     if (i > tickBegin) {
  //       if (ticks[i - tickBegin].close > ticks[i - tickBegin - 1].close) {
  //         avs -= ticks[i - tickBegin].vol;
  //       } else if (ticks[i - tickBegin].close < ticks[i - tickBegin - 1].close) {
  //         bvs -= ticks[i - tickBegin].vol;
  //       } else {
  //         cvs -= ticks[i - tickBegin].vol;
  //       }
  //     }
  //     let maavg = function (ticks, days) {
  //       let maSum = 0, p = 0;
  //       for (let i = 0; i < ticks.length; i++) {
  //         maSum += ticks[i];
  //       }
  //       return maSum / days;
  //     };
  //     if (i >= mavrBegin) {
  //       mavr.push(maavg(vr.slice(start, end), M));
  //       start++;
  //       end++;
  //     } else {
  //       mavr.push(null);
  //     }
  //   }
  //   let vr1 = [{ value: 0 }], mavr1 = [{ value: 0 }]
  //   for (let i = 0; i < vr.length; i++) {
  //     vr1.push({ value: vr[i + 1] });
  //     mavr1.push({ value: mavr[i + 1] })

  //   }
  //   vr = vr1
  //   mavr = mavr1
  //   this.setStore({
  //     vr,
  //     mavr,
  //     period,
  //     code
  //   })
  // }

  getVrData() {
    let volVal = [], closeVal = []
    const { last_px, business_amount, code, period, kline } = this.store;
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH,
      KLINE_ONEYEAR,
      KLINE_PRICE_TYPE
    ].some(a => a === period)
    if (isKLine) {
      closeVal = kline.map(a => a.close_px);
      volVal = kline.map(a => a.business_amount)
    } else {
      closeVal = last_px.map(a => a.value)
      volVal = business_amount.map(a => a.value)
    }
    const len = closeVal.length;
    let thvol = [], tlvol = [], tqvol = [], th = [], tl = [], tq = []
    for (let i = 0; i < len; i++) {
      if (closeVal[i] > closeVal[i - 1]) {
        thvol.push(volVal[i])
        tlvol.push(0)
        tqvol.push(0)
      } else if (closeVal[i] < closeVal[i - 1]) {
        tlvol.push(volVal[i])
        thvol.push(0)
        tqvol.push(0)
      } else {
        tqvol.push(volVal[i])
        thvol.push(0)
        tlvol.push(0)
      }
    }
    for (let i = 0; i < len; i++) {
      let thsum = 0, tlsum = 0, tqsum = 0
      if (i < 26) {
        thvol.slice(0, i).forEach(val => {
          thsum += val;
        })
        tlvol.slice(0, i).forEach(val => {
          tlsum += val
        })
        tqvol.slice(0, i).forEach(val => {
          tqsum += val
        })
      } else {
        thvol.slice(0, i + 1).slice(-26).forEach(val => {
          thsum += val
        })
        tlvol.slice(0, i + 1).slice(-26).forEach(val => {
          tlsum += val
        })
        tqvol.slice(0, i + 1).slice(-26).forEach(val => {
          tqsum += val
        })
      }
      th.push(thsum)
      tl.push(tlsum)
      tq.push(tqsum)
    }

    let vr = [], mavr = [], vr1 = [{ value: 0 }], mavr1 = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      //  VR:100*(TH*2+TQ)/(TL*2+TQ);
      // MAVR:MA(VR,M);
      vr.push(100 * (th[i] * 2 + tq[i]) / (tl[i] * 2 + tq[i]))
    }

    for (let i = 0; i < len; i++) {
      let mavrsum = 0
      if (i < 6) {
        vr.slice(0, i).forEach(val => {
          mavrsum += val
        })
      } else {
        vr.slice(0, i + 1).slice(-6).forEach(val => {
          mavrsum += val
        })
      }
      mavr.push(mavrsum / 6)
    }
    for (let i = 0; i < len; i++) {
      vr1.push({ value: vr[i] })
      mavr1.push({ value: mavr[i] })
    }
    for (let i = 0; i < len; i++) {
      vr[i] = vr1[i]
      mavr[i] = mavr1[i]
    }
    mavr[0] = { value: NaN }
    mavr[1] = { value: NaN }
    let params = 'VR(26,6)'
    this.setStore({
      vr,
      mavr,
      period,
      code,
      params
    })
  }
  /*
   DMA 只是以收盘价（CLOSE）作为唯一的数据源，进行平均算数
   DIF:MA(CLOSE,N1)-MA(CLOSE,N2);
   输出DIF:收盘价的N1日简单移动平均-收盘价的N2日简单移动平均；
   DIFMA:MA(DIF,M);
   输出均线DIF:DIF的M日简单移动平均；
   默认参数：N1=10，N2=50，M=10
   */

  getDmaData() {
    let data;
    const { last_px, code, period, kline } = this.store;
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH
    ].some(a => a === period);

    if (isKLine) {
      data = kline.map(a => a.close_px);
    } else {
      data = last_px.map(a => a.value);
    }
    // DIF:MA(CLOSE,N1)-MA(CLOSE,N2);
    const len = data.length;
    let dif10 = [], dif50 = [], dif = [], difma = []
    for (let i = 0; i < len; i++) {
      let dif10Sum = 0
      if (i < 10) {
        data.slice(0, i).forEach(val => {
          dif10Sum = null
        })
      } else {
        data.slice(0, i + 1).slice(-10).forEach(val => {
          dif10Sum += val
        })
      }
      dif10.push(dif10Sum / 10)
    }
    for (let i = 0; i < len; i++) {
      let dif50Sum = 0
      if (i < 50) {
        data.slice(0, i).forEach(val => {
          dif50Sum = null
        })
      } else {
        data.slice(0, i + 1).slice(-50).forEach(val => {
          dif50Sum += val
        })
      }
      dif50.push(dif50Sum / 50)
    }

    for (let i = 50; i < len; i++) {
      dif[i] = dif10[i] - dif50[i]
    }
    for (let i = 0; i < len; i++) {
      let difmasum = 0
      if (i < 10) {

        dif.slice(0, i).forEach(val => {
          val = null,
            difmasum = val
        })
      } else {
        dif.slice(0, i + 1).slice(-10).forEach(val => {
          difmasum += val
        })
      }
      difma.push(difmasum / 10)
    }
    let difma60 = []
    for (let i = 60; i < len; i++) {
      difma60[i] = difma[i]
    }
    difma = difma60
    let dif1 = [{ value: 0 }], difma1 = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      dif1[i] = { value: dif[i] }
      difma1[i] = { value: difma[i] }
    }
    dif = dif1
    difma = difma1
    let params = 'DMA(10,50,10)'
    this.setStore({
      code,
      period,
      dif,
      difma,
      params
    })
  }
  getObvData() {
    let data;
    const { business_amount, code, period, kline } = this.store
    let closeData = [], amount = []
    kline.forEach(val => {
      closeData.push(val.close_px)
      amount.push(val.business_amount)
    })
    let closePx = closeData[0]
    let obvsum = 0, m = 30, obvArr = []
    const len = kline.length
    let obv = [{ value: 0 }], maobv = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      if (closeData[i] == closePx) {
        //va.push(amount[i])
        // va.push(0)
        obvsum += 0
      } else {
        if (closeData[i] > closePx) {
          //va.push(amount[i])
          obvsum += amount[i]
        } else {
          //va.push(amount[i]*(-1))
          obvsum -= amount[i]
        }
        //va.push(-amount[i])
      }
      obvArr.push(obvsum)
      closePx = closeData[i]
    }

    let mobvArr = []
    for (let i = 0; i < len; i++) {
      let maobvSum = 0
      if (i < 30) {
        obvArr.slice(0, i).forEach(val => {
          maobvSum = null
        })
        mobvArr.push(maobvSum)
      } else {
        obvArr.slice(0, i + 1).slice(-30).forEach(val => {
          maobvSum += val
        })
        mobvArr.push(maobvSum)
      }
    }

    for (let i = 0; i < len; i++) {
      obv[i] = { value: obvArr[i] }
      maobv[i] = { value: mobvArr[i] / 30 }
    }
    let params = 'OBV(30)'
    this.setStore({
      obv,
      maobv,
      code,
      period,
      params

    })
  }
  getDmiData() {
    let data;
    const { close_px, high_px, low_px, period, kline, code } = this.store
    let closeData = [], highData = [], lowData = []

    kline.forEach(val => {
      closeData.push(val.close_px)
      highData.push(val.high_px)
      lowData.push(val.low_px)
    })

    // MTR:=SUM(MAX(MAX(HIGH-LOW,ABS(HIGH-REF(CLOSE,1))),ABS(REF(CLOSE,1)-LOW)),N);
    const len = kline.length;
    let hln = 0, hcn = 0, lcn = 0, max1 = 0, max2 = [], mtr = [], hd = [], ld = [], newArr = []
    for (let i = 1; i < len; i++) {
      hln = highData[i] - lowData[i]
      hcn = Math.abs(highData[i] - closeData[i - 1])
      lcn = Math.abs(closeData[i - 1] - lowData[i])
      max1 = Math.max(hln, hcn)
      max2[i] = Math.max(max1, lcn)
      hd[i] = highData[i] - highData[i - 1]
      ld[i] = lowData[i - 1] - lowData[i]
    }
    for (let i = 0; i < len; i++) {
      let mtrSum = 0
      if (i < 14) {
        max2.slice(0, i).forEach(val => {
          val = null
          mtrSum = val
        })
      } else {
        max2.slice(0, i + 1).slice(-14).forEach(val => {
          mtrSum += val
        })
      }
      mtr.push(mtrSum)
    }

    let dmpArr = [], dmmArr = [], dmp = [], dmm = []
    for (let i = 0; i < len; i++) {
      if (hd[i] > 0 && hd[i] > ld[i]) {
        dmpArr.push(hd[i])
      } else {
        dmpArr.push(0)
      }
    }

    for (let i = 0; i < len; i++) {
      if (ld[i] > 0 && ld[i] > hd[i]) {
        dmmArr.push(ld[i])
      } else {
        dmmArr.push(0)
      }
    }
    for (let i = 0; i < len; i++) {
      let dmpSum = 0
      if (i < 14) {
        dmpArr.slice(0, i).forEach(val => {
          dmpSum = 0
        })
      } else {
        dmpArr.slice(0, i + 1).slice(-14).forEach(val => {
          dmpSum += val
        })
      }
      dmp.push(dmpSum)
    }

    for (let i = 0; i < len; i++) {
      let dmmSum = 0
      if (i < 14) {
        dmmArr.slice(0, i).forEach(val => {
          dmmSum += 0
        })
      } else {
        dmmArr.slice(0, i + 1).slice(-14).forEach(val => {
          dmmSum += val
        })
      }
      dmm.push(dmmSum)
    }
    let pdi = [], mdi = []
    for (let i = 0; i < len; i++) {
      pdi[i] = dmp[i] * 100 / mtr[i]
      mdi[i] = dmm[i] * 100 / mtr[i]
    }
    //  ADX: MA(ABS(MDI-PDI)/(MDI+PDI)*100,M);
    let subMP = [], adxArr = [], adx = [], adxr = []
    for (let i = 0; i < len; i++) {
      subMP[i] = Math.abs(mdi[i] - pdi[i]) / (mdi[i] + pdi[i]) * 100
    }

    for (let i = 0; i < len; i++) {
      let sumadx = 0
      if (i < 6) {
        subMP.slice(0, i).forEach(val => {
          sumadx = null
        })

      } else {
        subMP.slice(0, i + 1).slice(-6).forEach(val => {
          sumadx += val
        })
      }
      adxArr.push(sumadx)
    }
    for (let i = 0; i < len; i++) {
      adx[i] = adxArr[i] / 6
    }
    for (let i = 0; i < len; i++) {
      if (i < 6) {
        adxr.push(null)
      } else {
        adxr[i] = (adx[i] + adx[i - 6]) / 2
      }
    }
    let pdi1 = [{ value: 0 }], mid1 = [{ value: 0 }], adx1 = [{ value: 0 }], adxr1 = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      pdi1[i] = { value: pdi[i] }
      mid1[i] = { value: mdi[i] }
      adx1[i] = { value: adx[i] }
      adxr1[i] = { value: adxr[i] }
    }
    pdi = pdi1
    mdi = mid1
    adx = adx1
    adxr = adxr1
    let params = 'DMI(14,6)'
    this.setStore({
      pdi,
      mdi,
      adx,
      adxr,
      code,
      period,
      params
    })
  }
  getPsyData() {
    let data;
    const { last_px, code, period, kline } = this.store;
    let closeData = [], closeData12 = []
    kline.forEach(val => {
      closeData.push(val.close_px)
    })
    const len = kline.length
    let psy = [], marr = []

    for (let i = 0; i < len; i++) {
      let m = 0
      if (i < 12) {
        closeData.slice(0, i).forEach(val => {
          m = 6
        })

      } else {
        closeData12 = closeData.slice(0, i + 1).slice(-13)
        for (let j = 1; j < closeData12.length; j++) {
          closeData12[j] > closeData12[j - 1] ? m++ : ''
        }
      }
      marr.push(m)
      psy.push(m / 12 * 100)
    }
    let psyMa = []
    for (let i = 0; i < len; i++) {
      let sum = 0
      if (i < 6) {
        psy.slice(0, i).forEach(val => {
          sum = 0
        })
      } else {
        psy.slice(0, i + 1).slice(-6).forEach(val => {
          sum += val
        })
      }
      psyMa.push(sum / 6)
    }
    let psy1 = [{ value: 0 }], psyMa1 = [{ value: 0 }]
    for (let i = 0; i < len; i++) {
      psy1[i] = { value: psy[i] }
      psyMa1[i] = { value: psyMa[i] }
    }
    psy = psy1
    psyMa = psyMa1
    let params = 'PSY(12,6)'
    this.setStore({
      psy,
      psyMa,
      period,
      code,
      params
    })
  }
  getBollData() {
    let data;
    const { last_px, code, period, kline } = this.store;
    /*
    K线的MACD与分时计算方式有差异
    分时取最新价计算，K线取收盘价计算
     */
    const isKLine = [
      KLINE_ONEMIN,
      KLINE_FIVEMIN,
      KLINE_FIFMIN,
      KLINE_THIMIN,
      KLINE_SIXMIN,
      KLINE_ONEDAY,
      KLINE_ONEWEEK,
      KLINE_ONEMONTH
    ].some(a => a === period);


    if (isKLine) {
      data = kline.map(a => a.close_px);

    } else {
      data = last_px.map(a => a.value);

    }
    let ticks = []

    data.forEach(val => {
      ticks.push(val)
    })
    let maDays = 20, tickBegin = maDays - 1, maSum = 0, p = 0;
    let ups = [], mas = [], lows = [];
    for (let i = 0; i < ticks.length; i++) {
      let c = ticks[i], ma, md, bstart, mdSum;
      maSum += parseFloat(c);
      if (i >= tickBegin) {
        maSum = maSum - p;
        ma = maSum / maDays;
        mas.push(ma);//boll
        bstart = i - tickBegin;
        p = parseFloat(ticks[bstart]);
        mdSum = ticks.slice(bstart, bstart + maDays).reduce(function(a, b) { return a + Math.pow(b - ma, 2); }, 0);
        md = Math.sqrt(mdSum / (maDays - 1));
        ups.push(ma + 2 * md);//ub
        lows.push(ma - 2 * md);//lb
      } else {
        //ugly constant, just keep the same type for client
        ups.push(null);//ub
        mas.push(null);//boll
        lows.push(null);//lb
      }
    }
    let boll = [{ value: 0 }], ub = [{ value: 0 }], lb = [{ value: 0 }]
    for (let i = 0; i < mas.length; i++) {
      boll.push({ value: mas[i] });
      ub.push({ value: ups[i] });
      lb.push({ value: lows[i] })
    }

    this.setStore({
      boll,
      ub,
      lb,
      code,
      period
    })
  }
  /*
  资金博弈数据
   */
  getFundCapitalData() {
    const { code, period, fundflow, circulation_amount } = this.store;
    const { fundflow_trademin_grp = [], fields } = fundflow;
    const superArr = [], largeArr = [], medArr = [], litArr = [];
    if (Util.isArray(fundflow_trademin_grp) && circulation_amount && fields) {
      const superInIndex = getIndex(fields, "super_in");
      const superOutIndex = getIndex(fields, "super_out");
      const largeInIndex = getIndex(fields, "large_in");
      const largeOutIndex = getIndex(fields, "large_out");
      const medInIndex = getIndex(fields, "medium_in");
      const medOutIndex = getIndex(fields, "medium_out");
      const litInIndex = getIndex(fields, "little_in");
      const litOutIndex = getIndex(fields, "little_out");
      fundflow_trademin_grp.forEach(item => {
        superArr.push({ value: (item[superInIndex] - item[superOutIndex]) / circulation_amount });
        largeArr.push({ value: (item[largeInIndex] - item[largeOutIndex]) / circulation_amount });
        medArr.push({ value: (item[medInIndex] - item[medOutIndex]) / circulation_amount });
        litArr.push({ value: (item[litInIndex] - item[litOutIndex]) / circulation_amount });
      });
    }
    this.setStore({
      code,
      period,
      superArr,
      largeArr,
      medArr,
      litArr
    });
  }
  /*
  资金流向
   */
  getFundFlowData() {
    const { code, period, fundflow, circulation_amount } = this.store;
    const { fundflow_trademin_grp = [], fields } = fundflow;
    const fundflowData = [];
    if (Util.isArray(fundflow_trademin_grp) && fields) {
      const superInIndex = getIndex(fields, "super_in");
      const superOutIndex = getIndex(fields, "super_out");
      const largeInIndex = getIndex(fields, "large_in");
      const largeOutIndex = getIndex(fields, "large_out");
      const medInIndex = getIndex(fields, "medium_in");
      const medOutIndex = getIndex(fields, "medium_out");
      const litInIndex = getIndex(fields, "little_in");
      const litOutIndex = getIndex(fields, "little_out");
      const baseFormat = 10000;
      fundflow_trademin_grp.forEach(item => {
        fundflowData.push({ value: (item[superInIndex] - item[superOutIndex] + item[largeInIndex] - item[largeOutIndex]) / baseFormat });
      });
    }
    this.setStore({
      code,
      period,
      fundflowData,
    });
  }
  /*
  KDJ
  n日RSV=（Cn－Ln）/（Hn－Ln）×100
  公式中，Cn为第n日收盘价；Ln为n日内的最低价；Hn为n日内的最高价。
   */
  getKDJData() {
    const { kline, code, period } = this.store;
    const len = kline.length;
    const n = 9;
    // if (!Util.isArray(kline) || len < n) {
    if (!Util.isArray(kline)) {
      return;
    }
    const kTemp = [], k = [], d = [], j = [];
    for (let i = 0, l = n - 2; i < l; i++) {
      kTemp.push(kline[i]);
      k.push({ value: '-' });
      d.push({ value: '-' });
      j.push({ value: '-' });
    }
    kTemp.push(kline[n - 2]);
    let lastRsv = '-';
    let lastKVal = 100;
    let lastDVal = 100;
    let lastJVal = 3 * lastKVal - 2 * lastDVal;
    k.push({ value: lastKVal });
    d.push({ value: lastDVal });
    j.push({ value: lastJVal });
    for (let i = n - 1; i < len; i++) {
      const pushItem = kline[i];
      kTemp.push(pushItem);
      const low = Math.min.apply(null, kTemp.map(a => a.low_px));
      const high = Math.max.apply(null, kTemp.map(a => a.high_px));
      const rsvVal = (pushItem.close_px - low) / (high - low) * 100;
      if (Number.isNaN(rsvVal) || !Number.isFinite(rsvVal)) {
        k.push({ value: lastKVal });
        d.push({ value: lastDVal });
        j.push({ value: lastJVal });
      } else {
        // 当日K值=2/3×前一日K值+1/3×当日RSV
        const curKVal = 2 / 3 * lastKVal + rsvVal / 3;
        // 当日D值=2/3×前一日D值+1/3×当日K值
        const curDVal = 2 / 3 * lastDVal + curKVal / 3;
        // J值=3*当日K值-2*当日D值
        const curJVal = 3 * curKVal - 2 * curDVal;
        k.push({ value: curKVal });
        d.push({ value: curDVal });
        j.push({ value: curJVal });
        [lastRsv, lastKVal, lastDVal, lastJVal] = [rsvVal, curKVal, curDVal, curJVal];
      }
      kTemp.shift();
    }
    let params = 'KDJ(9,3,3)'
    this.setStore({
      code,
      period,
      k,
      d,
      j,
      params
    });
  }
  /*
  ENE
   */
  getENEData() {
    const { kline, code, period } = this.store;
    const len = kline.length;
    if (!Util.isArray(kline) || len < n) {
      return;
    }
    const n = 10;
    const m1 = 11, m2 = 9;
    const upper = [], lower = [], ene = [], tempArr = [];
    for (let i = 0; i < n - 1; i++) {
      tempArr.push(kline[i].close_px);
      upper.push({ value: '-' });
      lower.push({ value: '-' });
      ene.push({ value: '-' });
    }
    for (let i = n - 1; i < len; i++) {
      tempArr.push(kline[i].close_px);
      const total = tempArr.reduce((prev, val) => {
        return prev + val;
      }, 0);
      const avg = total / n;
      const upperVal = (1 + m1 / 100) * avg;
      const lowerVal = (1 - m2 / 100) * avg;
      const eneVal = (upperVal + lowerVal) / 2;
      upper.push({ value: upperVal });
      lower.push({ value: lowerVal });
      ene.push({ value: eneVal });
      tempArr.shift();
    }
    this.setStore({
      code,
      period,
      upper,
      lower,
      ene
    });
  }
  /*
  JQJZ
   */
  async getJQJZData(code, period, min_time = []) {
    const len = min_time.length;
    const hasModule = this.store.mainChain.has(a => a.key === JZ_KEY);
    // 分时图调用1分钟k线获取9转信息
    // 有模块功能，有用户权限时请求1分钟K线
    if (hasModule && len > 4) {
      const date = min_time[0].toString();
      const day = date.substr(0, 8);
      const time = date.substr(8);
      // const day = date.split(' ')[0].replace(/\//ig, '');
      // const time = date.split(' ')[1].replace(/:/ig, '');
      let { code: klineCode, jqRiseList, jqFallList } = await this.klineDb.getOneMinKline(code, len, day, time);
      // 分时包含0930数据，1分钟K线0931开始
      jqFallList.forEach(item => {
        item.index = item.index + 1;
      });
      jqRiseList.forEach(item => {
        item.index = item.index + 1;
      });
      // 分时只展示9或者最新信号
      if (jqRiseList.length > 1) {
        const popItem = jqRiseList.slice(-1);
        jqRiseList = [
          ...jqRiseList.slice(0, -1).filter(item => item.value === 9),
          ...popItem
        ];
      }
      if (jqFallList.length > 1) {
        const popItem = jqFallList.slice(-1);
        jqFallList = [
          ...jqFallList.slice(0, -1).filter(item => item.value === 9),
          ...popItem
        ];
      }
      return { code: klineCode, period, jqRiseList, jqFallList };
    }
    return { code, period, jqRiseList: [], jqFallList: [] };
  }
  /**
   * XJDB
   */
  async getXJDBData() {
    const { error_code, ...signals } = await this.klineDb.getKlineSignal();
    if (Util.isValidNumber(error_code)) {
      // 用户没电波权限
      const { FOOT_STATUS } = this.store;
      for (let val of Object.values(FOOT_STATUS)) {
        const item = val.find(a => a.key === ELECTRIC_KEY);
        if (item) {
          item.noauth = true;
        }
      }
    }
    return signals;
  }
  /**
   * 鸿鹄震荡
   */
  async getSwanData() {
    const { swanSignals, min_time } = this.store;
    swanSignals.forEach(signal => {
      const index = min_time.findIndex(a => a === signal.appear_time);
      if (index > -1) {
        signal.index = index;
      }
    });
  }
  setStore(args) {
    // 校验数据一致性
    const { code, period } = this.store;
    if (!Util.isNull(args)) {
      if (args.code === code && args.period === period) {
        for (let key in args) {
          this.store[key] = args[key];
        }
      }
    }
  }
}
